import { Formik, type FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { object, string } from 'yup';
import {
    Alert,
    AlertButton,
    Child,
    Container,
    Heading,
    List,
    ListItem,
    Spinner,
    StyledButton,
    Subtitle,
    Title,
} from '../components';
import { darkText, successGreen } from '../models/colors';
import { DataSharingAgreementService, FleetCompanyService, useFleetCompanyForm, useIdentity, useNewAgreementValues, usePdfViewer } from '../services';
import { agreement } from '../text';
import type { INewAgreementValues, ISignAgreementValues } from '../types';
import { isEmpty, parseQueryString } from '../utils';
import { AgreementForm, schema } from './agreement/AgreementForm';
import { SignForm } from './agreement/SignForm';

export const Agreement = () => {
	const match = useParams();

	const route = () => {
		switch (match.path) {
			case `new`:
				return <NewAgreement />;
			case `update`:
				return <UpdateAgreement />;
			case `sign`:
				return <SignAgreement />;
			case `view`:
				return <ViewAgreement />;
			case `success`:
				return <SuccessAgreement />;
			default:
				return <Navigate to="/agreement/new" />;
		}
	};

	return <>{route()}</>;
};

const NewAgreement = () => {
	const initialValues: INewAgreementValues = {
		tsp: '',
		fleetIdentifier: '',
		name: '',
		address: '',
		city: '',
		state: '',
		country: '',
		zip: '',
		phone: '',
		representativeName: '',
		representativeTitle: '',
		representativeEmail: '',
		representativePhone: '',
	};
	const navigate = useNavigate();
    const { newAgreement } = useNewAgreementValues(initialValues);

	return (
		<Container container>
            <Child style={{ paddingBottom: '24px' }}>
				<Title>{agreement.newAgreement.title}</Title>
				<Subtitle>{agreement.newAgreement.subtitle}</Subtitle>
				<Subtitle style={{ color: darkText }}>{agreement.newAgreement.fleetId}</Subtitle>
			</Child>
            <Child container>
                {newAgreement.isComplete ? (
					<Formik
                        initialValues={newAgreement.data}
						validationSchema={schema}
						onSubmit={(v: INewAgreementValues) => {
							FleetCompanyService.createFleetCompany(v).then(
								res =>
									DataSharingAgreementService.createDataSharingAgreement(res).then(
										_ => navigate('/agreement/sign', { state: { fleetCompanyId: res.id } }),
										e => Promise.reject(e)
									),
								err => Promise.reject(err)
							);
						}}
					>
						{(props: FormikProps<INewAgreementValues>) => (
							<AgreementForm
								onSave={(v: INewAgreementValues) =>
									FleetCompanyService.createFleetCompany(v).then(
										_ => navigate('/'),
										err => Promise.reject(err)
									)
								}
								{...props}
							/>
						)}
					</Formik>
				) : (
					<Spinner />
				)}
			</Child>
		</Container>
	);
};

const UpdateAgreement = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const query = parseQueryString(location.search, { id: '' });
	const fleetId = query && query.id !== undefined ? query.id : '';
    const { identity } = useIdentity();
	const { fleetCompanyForm } = useFleetCompanyForm(identity.data.id, fleetId);
	const [partialData, setPartialData] = useState<INewAgreementValues>();

	useEffect(() => {
		if (!fleetCompanyForm.isComplete)
			return;

		if (!fleetCompanyForm.data)
			return;

		setPartialData(fleetCompanyForm.data as INewAgreementValues);
	}, [fleetCompanyForm.isComplete, fleetCompanyForm.data]);

	return (
		<Container container>
            <Child style={{ paddingBottom: '24px' }}>
				<Heading title={agreement.newAgreement.title} subtitle={agreement.newAgreement.subtitle} />
			</Child>
            <Child container>
                {fleetCompanyForm.isComplete && partialData ? (
					<Formik
						initialValues={partialData}
						validationSchema={schema}
						onSubmit={(v: INewAgreementValues) => {
							FleetCompanyService.updateFleetCompany(v, fleetId).then(
								res =>
									DataSharingAgreementService.createDataSharingAgreement(res).then(
										_ => navigate('/agreement/sign', { state: { fleetCompanyId: res.id } }),
										e => Promise.reject(e)
									),
								err => Promise.reject(err)
							);
						}}
					>
						{(props: FormikProps<INewAgreementValues>) => (
							<AgreementForm
								onSave={(v: INewAgreementValues) => {
									FleetCompanyService.updateFleetCompany(v, fleetId).then(
										_ => navigate('/'),
										err => Promise.reject(err)
									);
								}}
								{...props}
							/>
						)}
					</Formik>
				) : (
					<Spinner />
				)}
			</Child>
		</Container>
	);
};

const SignAgreement = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { pdfViewer } = usePdfViewer();
	return (
		<Container container>
            <Child>
				<Heading title={agreement.signAgreement.title} />
			</Child>
            <Child style={{ height: '512px', width: '100%' }}>
				{pdfViewer.isComplete ? (
					<iframe
						title={`PDF of Agreement`}
						src={`https://mozilla.github.io/pdf.js/web/viewer.html?file=${window.location.origin}${pdfViewer.data}`}
						width="100%"
						height="100%"
					/>
				) : null}
			</Child>
            <Child>
				<Formik
					initialValues={{ signature: '', timestamp: new Date().toISOString() }}
					validationSchema={object({
						signature: string().required(),
						timestamp: string().required(),
					})}
					onSubmit={(v: ISignAgreementValues) => {
						DataSharingAgreementService.updateDataSharingAgreement(v.signature, location.state).then(
							_ => navigate('/agreement/success'),
							err => Promise.reject(err)
						);
					}}
				>
					{(props: FormikProps<ISignAgreementValues>) => <SignForm {...props} />}
				</Formik>
			</Child>
		</Container>
	);
};

const ViewAgreement = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const query = parseQueryString(location.search, { id: '' });
	const idQuery = query && query.id !== undefined ? query.id : '';
	const { pdfViewer } = usePdfViewer(idQuery, false);
	return (
		<Container container>
			{isEmpty(idQuery) ? (
                <Child>
					<Alert
						message={agreement.viewAgreement.error}
						action={<AlertButton label="GO BACK" onClick={() => navigate('/')} />}
					/>
				</Child>
			) : (
				<>
                    <Child>
						<Heading title={agreement.viewAgreement.title} />
					</Child>
                    <Child style={{ height: '512px', width: '100%' }}>
						{pdfViewer.isComplete ? (
							<iframe
								title={`PDF of Agreement`}
								src={`https://mozilla.github.io/pdf.js/web/viewer.html?file=${window.location.origin}${pdfViewer.data}`}
								width="100%"
								height="100%"
							/>
						) : null}
					</Child>
                    <Child container justify="center">
						<StyledButton onClick={() => navigate(-1)}>BACK</StyledButton>
					</Child>
				</>
			)}
		</Container>
	);
};

const SuccessAgreement = () => {
	const navigate = useNavigate();
	const { pdfViewer } = usePdfViewer();
	return (
		<Container container>
			<Title>{agreement.successAgreement.title}</Title>
			<List>
				<ListItem textStyle={{ color: successGreen }} text={agreement.successAgreement.changeAgreement} />
				<ListItem textStyle={{ color: successGreen }} text={agreement.successAgreement.welcomeEmail} />
				<ListItem textStyle={{ color: successGreen }} text={agreement.successAgreement.viewInfo} />
				<ListItem textStyle={{ color: successGreen }} text={agreement.successAgreement.neglectfulTsp} />
			</List>
                <Child style={{ height: '512px', width: '100%' }}>
				{pdfViewer.isComplete ? (
					<iframe
						title={`PDF of Agreement`}
						src={`https://mozilla.github.io/pdf.js/web/viewer.html?file=${window.location.origin}${pdfViewer.data}`}
						width="100%"
						height="100%"
					/>
				) : null}
			</Child>
            <Child container justify="center">
				<StyledButton onClick={() => navigate('/')}>CONTINUE</StyledButton>
			</Child>
		</Container>
	);
};
